import { AlertTriangle, ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useLocation } from '#router'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Video } from '@/components/ui/video'
import { isUngatedReason } from '@/lib/audition/calcom-eligibility'
import { AUDITION_INTAKE_CLOSED } from '@/lib/audition/intake'
import {
  ARCHIVE_GROUP_LABELS,
  type AuditionDecision,
  isRetryableBooking,
} from '@/lib/audition/queue'
import { formatSlot } from '@/lib/audition/slots'
import type { TimelineTone } from '@/lib/audition/timeline'
import { type RouterOutputs, trpc } from '@/lib/trpc'
import { EvidenceCard } from './evidence-card'
import { useAuditionInvalidation, useReviewLens } from './hooks/use-auditions'
import { CallTag, laneLabel, PaidTag, StageTag, WaitLabel } from './labels'
import { archiveHref, QUEUE_HREF, readArchiveOrigin } from './queue-parts'

type Detail = RouterOutputs['admin']['auditions']['detail']
type ProposalRow = Detail['proposals'][number]
type BookingRow = Detail['bookings'][number]

/**
 * When each recovery action applies. Named rather than inlined because the
 * wrapper that decides whether to render the row's button group has to ask
 * the same two questions, and a third action would otherwise mean editing
 * the predicate in two places.
 */
// Mirrors `isRetryableBooking`, which mirrors the server's own claim: the
// retry requires a still-`selected` proposal, so on a call already marked
// held or no-showed this button could only ever answer 412.
const canRetryBooking = (p: ProposalRow) =>
  isRetryableBooking({
    status: p.status,
    calendarStatus: p.calendarStatus,
    // The row's real slot, not a hard-coded null. The predicate ignores it
    // today; passing a value the proposal never had would let it start
    // mattering without this call site changing.
    selectedSlot: p.selectedSlot ? new Date(p.selectedSlot) : null,
  })

const PROPOSAL_STATUS_LABELS: Record<ProposalRow['status'], string> = {
  proposed: 'Times offered',
  selected: 'Time picked',
  held: 'Call held',
  no_show: 'No-show',
  cancelled: 'Withdrawn',
}

/**
 * cal.com's own words for a booking (LG-902). Distinct from the proposal
 * labels above even where the status names coincide: "Withdrawn" is
 * something we did to an offer, while a cal.com booking is "Cancelled" by
 * whoever cancelled it, and a reviewer should not have to guess which.
 */
const BOOKING_STATUS_LABELS: Record<BookingRow['status'], string> = {
  pending: 'Awaiting approval',
  booked: 'Booked',
  declined: 'Declined',
  cancelled: 'Cancelled',
  held: 'Call held',
  no_show: 'No-show',
}

/**
 * cal.com reports the location of every integration booking as an app slug,
 * not as the name a person would recognise. Anything unlisted falls through
 * unchanged, because a free-text location (a phone number, an address) is
 * already the display form.
 */
const LOCATION_LABELS: Record<string, string> = {
  'integrations:daily': 'Cal Video',
  'integrations:zoom': 'Zoom',
  'integrations:google:meet': 'Google Meet',
  'integrations:msteams': 'Microsoft Teams',
  attendeeInPerson: 'In person',
  inPerson: 'In person',
  phone: 'Phone',
  userPhone: 'Phone',
}

const TONE_STYLES: Record<TimelineTone, { dot: string; text: string }> = {
  neutral: { dot: 'bg-muted-foreground', text: 'text-foreground' },
  good: { dot: 'bg-emerald-500', text: 'text-foreground' },
  warn: {
    dot: 'bg-amber-500',
    text: 'text-amber-700 dark:text-amber-400 font-medium',
  },
  miss: { dot: 'bg-destructive', text: 'text-destructive font-medium' },
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
    </section>
  )
}

/**
 * Admin review + decision surface for one applicant (LG-414 rebuild of the
 * LG-118 slice).
 *
 * Everything a reviewer needs to decide is on this page — the owed decision
 * as the header CTA, the answers as prose, the derived timeline with its
 * flags, the call history, and the cooldown — because the alternative is
 * deciding from a list row and a hunch.
 *
 * Every action invalidates the detail query on success rather than patching
 * local state: the server owns the stage machine, so what it says after the
 * write is the truth.
 */
export function AuditionDetail({ submissionId }: { submissionId: string }) {
  const detail = trpc.admin.auditions.detail.useQuery({ submissionId })
  const invalidate = useAuditionInvalidation(submissionId)
  const [lens] = useReviewLens()
  // Where the row was opened from, when that was an archive (LG-1052): an
  // admin mid-search in the rejected archive used to be sent back to the
  // live queue, losing the group and the search. Nothing but the way back
  // reads this — the page itself is the same whichever list it came from, so
  // nothing here has to guard the URL; `readArchiveOrigin` says what it hands
  // back for a `from` nobody wrote.
  const location = useLocation()
  const origin = readArchiveOrigin(location.searchParams)
  const [confirming, setConfirming] = useState<'accept' | 'reject' | null>(null)
  const [rejectAcknowledged, setRejectAcknowledged] = useState(false)

  // Every action shares this: refetch on success, and SAY SO on failure.
  // Without the onError a server-side guard rejection was byte-identical to
  // success — the stale stage just stayed on screen looking applied.
  const mutationOptions = {
    onSuccess: invalidate,
    // Widened to the one field every tRPC error carries, so a single
    // options object serves every mutation below rather than each needing a
    // handler typed to its own error.
    onError: (err: { message: string }) => toast.error(err.message),
  }

  const accept = trpc.admin.auditions.accept.useMutation({
    ...mutationOptions,
    onSuccess: (result) => {
      setConfirming(null)
      // The stage commits to Postgres whether or not Clerk answered, so an
      // unqualified "Accepted" over a role that never moved is somebody
      // locked out of exactly what they were just let into — the same
      // partial-failure reporting `reject` already does for the refund.
      //
      // `'failed'` only. `'skipped'` is the ordinary case — an applicant
      // with no linked account, an admin who is also a test applicant, a
      // client re-applying — and reporting it would put a red toast on a
      // decision that worked perfectly.
      if (result.roleSync === 'failed')
        toast.error(
          'Accepted, but their account access did not update — re-run the decision or fix the role by hand.',
        )
      else
        toast.success(
          result.outcome === 'approved'
            ? 'Accepted — they are in the cohort and their credits are granted.'
            : 'Accepted — payment is available through their existing application link. No email was sent.',
        )
      return invalidate()
    },
  })
  const reject = trpc.admin.auditions.reject.useMutation({
    ...mutationOptions,
    onSuccess: (result) => {
      setConfirming(null)
      // The mutation reports each money-side step separately on purpose, and
      // silently succeeding while a refund failed is the one outcome that
      // costs a real person real money.
      if (result.refundFailed)
        toast.error(
          'Rejected, but the refund failed — refund by hand in Stripe.',
        )
      else if (result.clawbackFailed)
        toast.error('Rejected and refunded, but the credit clawback failed.')
      else if (!result.cooldownApplied)
        toast.error('Rejected, but the 90-day block was not set.')
      else if (result.reapplyDateUncertain)
        toast.error(
          'Rejected and blocked, but we could not confirm the reapply date.',
        )
      else toast.success('Rejected.')
      return invalidate()
    },
  })
  const markCallHeld =
    trpc.admin.auditions.markCallHeld.useMutation(mutationOptions)
  const retryCalendarBooking =
    trpc.admin.auditions.retryCalendarBooking.useMutation(mutationOptions)
  const setCooldown = trpc.admin.cooldowns.set.useMutation(mutationOptions)

  if (detail.isError) {
    return (
      <p role="alert" className="text-destructive p-6">
        We couldn't load this application. Reload to try again.
      </p>
    )
  }
  if (!detail.data) {
    return <p className="text-muted-foreground p-6">Loading application…</p>
  }

  const {
    submission,
    proposals,
    bookings,
    creditBalance,
    cooldown,
    queue,
    decisions,
    timeline,
    timelineFlags,
    glance,
    prose,
    motivationQuote,
  } = detail.data
  // Aliased so the null check narrows inside the callbacks below; property
  // narrowing doesn't survive into a closure, which is what the old
  // `as string` assertion was papering over.
  const linkedUserId = submission.userId
  const deciding = accept.isPending || reject.isPending

  /** Why each decision is or isn't available, in the reviewer's words. */
  const decisionReason = (decision: AuditionDecision): string => {
    if (decision === 'accept') {
      if (!decisions.accept) {
        if (submission.stage === 'payment_unlocked')
          return AUDITION_INTAKE_CLOSED
            ? 'Already accepted, but new payments are closed — they cannot pay now.'
            : 'Already accepted — waiting on them to pay.'
        if (submission.stage === 'client') return 'Already in the cohort.'
        if (submission.stage === 'rejected') return 'Already rejected.'
        if (submission.stage === 'wizard_in_progress')
          return 'Not an applicant yet — they have not submitted.'
        // The server refuses an unpaid accept while intake is closed: it
        // would unlock a payment the applicant can no longer make.
        if (AUDITION_INTAKE_CLOSED && !submission.hasPaid)
          return 'Payments are closed — only applicants who already paid can be accepted.'
        // Named rather than assumed: the server owns which stages can be
        // accepted, and a stage added there would otherwise be explained to
        // the admin as an unsubmitted application.
        return 'Not available at this stage.'
      }
      return submission.hasPaid
        ? 'They have already paid — this puts them straight into the cohort.'
        : 'Enables payment through their existing application link. No email is sent.'
    }
    if (decision === 'book_call') {
      return decisions.book_call
        ? 'Sends a scheduling link. Nothing else changes, and the email never says we are unsure.'
        : 'Not applicable — this application is finished.'
    }
    return decisions.reject
      ? 'Refunds payment, claws back credits, withdraws invitations, blocks re-applying for 90 days. Irreversible.'
      : 'Not applicable — this application is finished.'
  }

  const runDecision = (decision: AuditionDecision) => {
    if (decision !== 'book_call') setConfirming(decision)
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <Link
        href={origin ? archiveHref(origin.group, origin.q) : QUEUE_HREF}
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft aria-hidden="true" className="size-4" />
        {origin
          ? `${ARCHIVE_GROUP_LABELS[origin.group]} archive`
          : 'Applications'}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <h1 className="flex flex-wrap items-center gap-3 text-2xl font-semibold">
            {`${submission.firstName} ${submission.lastName}`}
            <StageTag stage={submission.stage} />
            {submission.hasPaid && <PaidTag />}
            <CallTag call={queue.call} stage={submission.stage} />
          </h1>
          <p className="text-muted-foreground text-sm">
            {submission.email}
            {submission.phone ? ` · ${submission.phone}` : null}
            {` · ${laneLabel(submission.lane)}`}
          </p>
          {/* All three off the one derivation. `submission.createdAt` told a
              resubmitted applicant they had waited a day, under a tooltip
              dated two months earlier. */}
          <WaitLabel
            days={queue.waitDays}
            overdue={queue.overdue}
            appliedAt={queue.appliedAt}
          />
        </div>
        {/* `decisions` carries the intake-closed override; the queue
            derivation does not, so it alone would offer an unpaid accept
            the server refuses. */}
        {queue.primaryDecision === 'accept' && decisions.accept && (
          <div className="text-right">
            <Button
              disabled={deciding}
              onClick={() => {
                const decision = queue.primaryDecision
                if (decision) runDecision(decision)
              }}
            >
              Accept
            </Button>
            <p className="text-muted-foreground mt-1 max-w-56 text-xs">
              {queue.owed}
            </p>
          </div>
        )}
      </header>

      {cooldown && (
        <section className="border-destructive/50 bg-destructive/5 flex flex-wrap items-center gap-4 rounded-md border p-4">
          <AlertTriangle
            aria-hidden="true"
            className="text-destructive size-5"
          />
          <p className="flex-1 text-sm">
            {`Blocked from re-applying (${cooldown.reason}) until ${new Date(cooldown.expiresAt).toLocaleDateString()}.`}
          </p>
          <Button
            variant="outline"
            disabled={setCooldown.isPending}
            onClick={() =>
              setCooldown.mutate({
                email: submission.email,
                clerkUserId: linkedUserId ?? undefined,
                days: 0,
                reason: cooldown.reason,
              })
            }
          >
            Lift cooldown
          </Button>
        </section>
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-8">
          {lens === 'motivation' && motivationQuote && (
            <blockquote className="bg-card rounded-md border p-4">
              <p className="text-primary mb-2 text-xs font-semibold uppercase tracking-wide">
                Motivation — what they'd be celebrating
              </p>
              <p className="text-base leading-relaxed">{`“${motivationQuote}”`}</p>
            </blockquote>
          )}

          <Section title="Timeline">
            <p className="text-muted-foreground text-xs">
              {timelineFlags.count === 0
                ? 'Smooth so far'
                : `${timelineFlags.count} ${timelineFlags.count === 1 ? 'flag' : 'flags'}`}
            </p>
            <ol className="space-y-2">
              {timeline.map((entry) => (
                <li
                  key={`${entry.at}-${entry.label}`}
                  className="grid grid-cols-[10px_90px_1fr] items-start gap-3 text-sm"
                >
                  <span
                    aria-hidden="true"
                    className={`mt-1.5 size-2.5 rounded-full ${TONE_STYLES[entry.tone].dot}`}
                  />
                  <span className="text-muted-foreground text-xs">
                    {new Date(entry.at).toLocaleDateString()}
                  </span>
                  <span className={TONE_STYLES[entry.tone].text}>
                    {entry.label}
                  </span>
                </li>
              ))}
            </ol>
          </Section>

          <Section title="Intro call">
            {/* cal.com bookings first: since LG-902 this is how calls
                actually get booked, and the proposal list below is the
                historical path nothing creates any more. */}
            {bookings.length > 0 && (
              <ul
                aria-label="Booked through cal.com"
                className="mb-3 space-y-3"
              >
                {bookings.map((booking) => {
                  const host = booking.hostName || booking.hostEmail
                  const location = booking.location
                    ? (LOCATION_LABELS[booking.location] ?? booking.location)
                    : null
                  const slot = formatSlot(
                    new Date(booking.startAt).toISOString(),
                  )
                  // A reschedule mints a new booking and cancels the old one,
                  // so without this the pair reads as an applicant who called
                  // a call off and then booked another — a fact a reviewer
                  // weighs.
                  const rescheduledFrom = booking.rescheduledFromUid
                    ? bookings.find(
                        (b) => b.bookingUid === booking.rescheduledFromUid,
                      )
                    : undefined

                  return (
                    <li
                      key={booking.id}
                      className="bg-card space-y-2 rounded-md border p-3 text-sm"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                          {BOOKING_STATUS_LABELS[booking.status]}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {slot}
                        </span>
                      </div>
                      <p className="text-muted-foreground text-xs">
                        {/* Who the round-robin rota actually assigned — the
                          question a round-robin link makes unanswerable
                          unless it is written down. One notion of "missing"
                          in the test and in the value: `hostName ?? …` would
                          print an empty name that `||` had already rescued,
                          suppressing the email we do have. */}
                        {host ? `With ${host}` : 'Host not recorded'}
                        {location ? ` · ${location}` : null}
                      </p>
                      {/* Both columns can be set on one row: a Cal Video call
                        nobody joined fires the guest trigger and the host
                        trigger. Saying "not the applicant" there would deny
                        the "No-show" heading directly above it. */}
                      {booking.hostNoShow && (
                        <p className="text-destructive text-xs font-medium">
                          {booking.status === 'no_show'
                            ? 'Neither side joined — our miss too.'
                            : 'We missed this call — not the applicant.'}
                        </p>
                      )}
                      {/* An ungated booking is `booked`, so the status-scoped
                        branch below could never render it — the recorder
                        wrote this warning and no screen showed it (LG-940).
                        It is louder than the others on purpose: it does not
                        describe this booking so much as report that the
                        cal.com toggle protecting every booking is off. */}
                      {isUngatedReason(booking.gateReason) && (
                        <p className="text-destructive text-xs font-medium">
                          Bypassed the eligibility gate — check that “requires
                          confirmation” is still on for the audition event type.{' '}
                          {booking.gateReason}
                        </p>
                      )}
                      {/* The reason is internal. It is the only thing that
                        separates our gate refusing a booking from a host
                        declining one by hand, and the applicant was
                        deliberately told neither. */}
                      {booking.gateReason &&
                        !isUngatedReason(booking.gateReason) &&
                        (booking.status === 'declined' ||
                          booking.status === 'pending') && (
                          <p className="text-muted-foreground text-xs italic">
                            {booking.gateReason}
                          </p>
                        )}
                      {rescheduledFrom && (
                        <p className="text-muted-foreground text-xs">
                          Rescheduled from the call booked for{' '}
                          {formatSlot(
                            new Date(rescheduledFrom.startAt).toISOString(),
                          )}
                          .
                        </p>
                      )}
                      {/* An email match is an inference and can attach a call
                        to the wrong person; the reviewer sees which it was
                        rather than being shown both as equally certain. */}
                      {booking.matchedBy === 'attendee_email' && (
                        <p className="text-muted-foreground text-xs">
                          Matched by email address, not by a booking link.
                        </p>
                      )}
                      {/* The url survives every later delivery, so a
                        cancelled, refused or still-pending booking keeps it.
                        Offering it would send an admin into a dead room —
                        and on a declined row it contradicts the status
                        printed directly above. */}
                      {booking.meetingUrl && booking.status === 'booked' && (
                        <a
                          href={booking.meetingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                          // Two bookings on one applicant is reachable — a
                          // reschedule alone produces a pair — and the proposal
                          // rows below emit this same visible text.
                          aria-label={`Join the video meeting for the call on ${slot}`}
                        >
                          Join video meeting
                        </a>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
            {proposals.length === 0 && bookings.length === 0 && (
              <p className="text-muted-foreground text-sm">
                No intro call on record.
              </p>
            )}
            {proposals.length > 0 && (
              <ul aria-label="Times we offered" className="space-y-3">
                {proposals.map((proposal) => {
                  // Both hooks are shared by every row, so `isPending` alone
                  // would grey out an action on a proposal nobody touched.
                  // `variables` is what identifies the row actually in flight
                  // — and two failed bookings on one applicant is reachable.
                  // That same reachability is why every button here carries a
                  // proposal-specific accessible name.
                  const offeredOn = new Date(
                    proposal.createdAt,
                  ).toLocaleDateString()
                  const bookedFor = proposal.selectedSlot
                    ? formatSlot(new Date(proposal.selectedSlot).toISOString())
                    : `the times offered on ${offeredOn}`
                  const retrying =
                    retryCalendarBooking.isPending &&
                    retryCalendarBooking.variables?.proposalId === proposal.id
                  const outcomePending =
                    markCallHeld.isPending &&
                    markCallHeld.variables?.proposalId === proposal.id

                  return (
                    <li
                      key={proposal.id}
                      className="bg-card space-y-2 rounded-md border p-3 text-sm"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">
                          {PROPOSAL_STATUS_LABELS[proposal.status]}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {`Offered ${offeredOn} · ${proposal.slots.length} times`}
                        </span>
                      </div>
                      {proposal.selectedSlot && (
                        <p className="text-muted-foreground">
                          {formatSlot(
                            new Date(proposal.selectedSlot).toISOString(),
                          )}
                        </p>
                      )}
                      <p className="text-muted-foreground text-xs">
                        {proposal.emailSentAt
                          ? 'Invitation email sent'
                          : 'Invitation email not sent'}
                        {` · calendar: ${proposal.calendarStatus}`}
                      </p>
                      {proposal.meetingUrl && (
                        <a
                          href={proposal.meetingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          Join video meeting
                        </a>
                      )}

                      {/* Each recovery action sits next to the status it
                          repairs, and appears only while that failure is live
                          — the server refuses both otherwise, and a button
                          that always 403s is worse than no button. */}
                      {canRetryBooking(proposal) && (
                        <div className="flex flex-wrap gap-2">
                          {canRetryBooking(proposal) && (
                            <Button
                              type="button"
                              variant="outline"
                              // Tracks the visible text, so the accessible
                              // name always contains it (WCAG 2.5.3 —
                              // otherwise "click Retrying" finds nothing).
                              aria-label={
                                retrying
                                  ? `Retrying calendar booking for ${bookedFor}`
                                  : `Retry calendar booking for ${bookedFor}`
                              }
                              disabled={retrying}
                              onClick={() =>
                                retryCalendarBooking.mutate({
                                  proposalId: proposal.id,
                                })
                              }
                            >
                              {retrying
                                ? 'Retrying…'
                                : 'Retry calendar booking'}
                            </Button>
                          )}
                        </div>
                      )}

                      {proposal.status === 'selected' && (
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            aria-label={`Mark the call booked for ${bookedFor} as held`}
                            disabled={outcomePending}
                            onClick={() =>
                              markCallHeld.mutate({
                                proposalId: proposal.id,
                                held: true,
                              })
                            }
                          >
                            Mark held
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            aria-label={`Mark the call booked for ${bookedFor} as a no-show`}
                            disabled={outcomePending}
                            onClick={() =>
                              markCallHeld.mutate({
                                proposalId: proposal.id,
                                held: false,
                              })
                            }
                          >
                            No-show
                          </Button>
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </Section>

          <Section title="Application">
            {prose.length === 0 ||
            prose.every((g) => g.items.every((i) => !i.answer)) ? (
              <p className="text-muted-foreground text-sm">
                Still filling in the form — nothing submitted yet.
              </p>
            ) : (
              prose.map((group) => (
                <div key={group.id} className="space-y-4">
                  <h3 className="text-primary text-sm font-semibold">
                    {group.title}
                  </h3>
                  {group.items.map((item) => (
                    <div key={item.name} className="max-w-[66ch]">
                      <p className="text-muted-foreground mb-1 text-xs font-medium">
                        {item.question}
                      </p>
                      {item.answer ? (
                        <p className="whitespace-pre-wrap text-sm leading-relaxed">
                          {item.answer}
                        </p>
                      ) : (
                        <p className="text-muted-foreground text-xs italic">
                          Not answered
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
            {submission.videoPlaybackId && (
              <Video playbackId={submission.videoPlaybackId} />
            )}
          </Section>
        </div>

        {/* Pinned, so it is capped to the viewport and scrolls on its own:
            with the Evidence card on top, a laptop-height screen would
            otherwise hold the decisions below its edge. */}
        <div className="space-y-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:self-start lg:overflow-y-auto">
          <EvidenceCard evidence={submission} />
          {glance.some((fact) => fact.value) && (
            <div className="bg-card rounded-md border p-4">
              <p className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide">
                At a glance
              </p>
              <dl className="divide-border divide-y text-sm">
                {glance.map((fact) => (
                  <div
                    key={fact.label}
                    className="grid grid-cols-[90px_1fr] gap-2 py-1.5"
                  >
                    <dt className="text-muted-foreground text-xs">
                      {fact.label}
                    </dt>
                    <dd
                      className={
                        fact.lens === lens ? 'text-primary font-semibold' : ''
                      }
                    >
                      {fact.value || '—'}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="bg-card space-y-3 rounded-md border p-4">
            <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
              Decisions
            </p>
            {(['accept', 'reject'] as const).map((decision) => (
              <div key={decision}>
                <Button
                  className="w-full"
                  variant={
                    decision === 'reject'
                      ? 'destructive'
                      : decision === queue.primaryDecision
                        ? 'default'
                        : 'outline'
                  }
                  disabled={!decisions[decision] || deciding}
                  onClick={() => runDecision(decision)}
                >
                  {decision === 'accept' ? 'Accept' : 'Reject…'}
                </Button>
                <p className="text-muted-foreground mt-1 text-xs leading-snug">
                  {decisionReason(decision)}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-card space-y-1 rounded-md border p-4">
            <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
              Credits
            </p>
            <p className="text-sm">{`${creditBalance} credits`}</p>
            {/* Read-only on purpose (LG-414): granting and refunding credits
                belongs to the credits admin surface (LG-413), and two places
                that can move a balance is how a balance stops being trusted. */}
            <p className="text-muted-foreground text-xs">
              Credits are granted and refunded in credits management, not here.
            </p>
          </div>
        </div>
      </div>

      <Dialog
        open={confirming === 'accept'}
        onOpenChange={(open) => !open && setConfirming(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {`Accept ${submission.firstName} ${submission.lastName}?`}
            </DialogTitle>
            <DialogDescription>
              {submission.hasPaid
                ? 'They have already paid, so this puts them straight into the cohort and grants their starting session credits.'
                : 'This enables payment through their existing application link. No email is sent. They join the cohort once they pay.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button
              disabled={accept.isPending}
              onClick={() => accept.mutate({ submissionId })}
            >
              {accept.isPending ? 'Accepting…' : 'Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirming === 'reject'}
        onOpenChange={(open) => {
          if (!open) {
            setConfirming(null)
            setRejectAcknowledged(false)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {`Reject ${submission.firstName} ${submission.lastName}?`}
            </DialogTitle>
            <DialogDescription>
              This is irreversible. Rejecting will, immediately:
            </DialogDescription>
          </DialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            <li>
              <strong className="font-medium">Refund</strong> any payment they
              have made
            </li>
            <li>
              <strong className="font-medium">Claw back</strong> any granted
              session credits
            </li>
            <li>
              <strong className="font-medium">Withdraw</strong> any outstanding
              call invitations
            </li>
            <li>
              <strong className="font-medium">Block re-applying</strong> for 90
              days
            </li>
          </ul>
          {/* An acknowledgement, not just a second click: `window.confirm`
              was one keystroke away from refunding real money, and every
              button on this page is the same shape. */}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={rejectAcknowledged}
              onChange={(event) => setRejectAcknowledged(event.target.checked)}
            />
            I understand this refunds money and cannot be undone
          </label>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirming(null)
                setRejectAcknowledged(false)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectAcknowledged || reject.isPending}
              onClick={() => reject.mutate({ submissionId })}
            >
              {reject.isPending ? 'Rejecting…' : 'Reject applicant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
